"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";

// Icons
import { 
  AiOutlineMenu, 
  AiOutlineClose, 
  AiOutlineMail, 
  AiOutlinePhone 
} from "react-icons/ai";
import { 
  HiChevronDown, 
  HiOutlineExternalLink,
  HiOutlineSparkles 
} from "react-icons/hi";
import { 
  FaCode, 
  FaPaintBrush, 
  FaPen, 
  FaChartLine, 
  FaDatabase, 
  FaShieldAlt, 
  FaLaptopCode,
  FaArrowRight,
  FaWhatsapp
} from "react-icons/fa";

// --- Data Configuration ---
const navigation = [
  { name: "Premium Courses", href: "/premium-courses/", type: "dropdown" },
  { name: "Resources", href: "/free-courses/", type: "link" },
  { name: "Events", href: "/events/", type: "link" },
  { name: "About", href: "/about/", type: "link" },
  { name: "Careers", href: "/career/", type: "link" },
  { name: "Blog", href: "/blog/", type: "link" },
  { name: "Contact", href: "/contact/", type: "link" },
];

const premiumCourses = [
  { name: "MERN Stack Web Dev", href: "/premium-courses/mern-stack-web-development", icon: <FaCode />, color: "text-blue-500", bg: "bg-blue-50" },
  { name: "UX/UI Design", href: "/premium-courses/uxperience-redefining-graphics-and-interface-design", icon: <FaPaintBrush />, color: "text-purple-500", bg: "bg-purple-50" },
  { name: "Video Editing", href: "/premium-courses/basic-video-editing-and-graphic-design", icon: <FaLaptopCode />, color: "text-pink-500", bg: "bg-pink-50" },
  { name: "Content Writing", href: "/premium-courses/academic-non-academic-and-news-content-writing", icon: <FaPen />, color: "text-orange-500", bg: "bg-orange-50" },
  { name: "Digital Marketing", href: "/premium-courses/digital-marketing-using-generative-ai-tools", icon: <FaChartLine />, color: "text-green-500", bg: "bg-green-50" },
  { name: "Data Science & AI", href: "/premium-courses/data-science-and-analytics-using-ai-and-chat-gpt", icon: <FaDatabase />, color: "text-indigo-500", bg: "bg-indigo-50" },
];

// --- Desktop Menu Component ---
const DesktopMenu = ({ currentPath, scrolled }) => {
  const [hovered, setHovered] = useState(null);

  return (
    <ul className="hidden lg:flex items-center gap-1">
      {navigation.map((item) => {
        const isActive = currentPath === item.href;
        
        return (
          <li 
            key={item.name}
            className="relative h-full"
            onMouseEnter={() => setHovered(item.name)}
            onMouseLeave={() => setHovered(null)}
          >
            <Link
              href={item.href}
              className={`relative px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 flex items-center gap-1.5 z-10
                ${isActive 
                  ? "text-blue-600 bg-blue-50" 
                  : scrolled 
                    ? "text-slate-600 hover:text-slate-900 hover:bg-slate-50" 
                    : "text-slate-300 hover:text-white hover:bg-white/10"
                }
              `}
            >
              {item.name}
              {item.type === "dropdown" && (
                <HiChevronDown className={`w-3 h-3 transition-transform duration-300 ${hovered === item.name ? "rotate-180" : ""}`} />
              )}
            </Link>

            {/* Mega Menu Dropdown */}
            {item.type === "dropdown" && (
              <AnimatePresence>
                {hovered === item.name && (
                  <motion.div
                    initial={{ opacity: 0, y: 15, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.98 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="absolute left-1/2 -translate-x-1/2 top-full mt-4 w-[750px] bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-blue-900/20 border border-white/50 p-2 z-50 overflow-hidden ring-1 ring-black/5"
                  >
                    <div className="flex">
                      <div className="w-2/3 p-5 grid grid-cols-2 gap-2">
                        <div className="col-span-2 pb-2 mb-2 border-b border-slate-100">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Career Tracks</span>
                        </div>
                        {premiumCourses.map((course) => (
                          <Link
                            key={course.name}
                            href={course.href}
                            className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors group"
                          >
                            <span className={`p-2 rounded-lg ${course.bg} ${course.color} group-hover:scale-110 transition-transform duration-300`}>
                              {course.icon}
                            </span>
                            <div>
                              <h4 className="font-semibold text-slate-700 text-sm group-hover:text-blue-600 transition-colors">
                                {course.name}
                              </h4>
                            </div>
                          </Link>
                        ))}
                        <div className="col-span-2 pt-2 mt-1 border-t border-slate-100">
                          <Link
                            href="/premium-courses/"
                            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors group"
                          >
                            View All Courses <FaArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                          </Link>
                        </div>
                      </div>
                      <div className="w-1/3 bg-slate-50/50 p-5 rounded-r-2xl flex flex-col justify-between border-l border-slate-100">
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <HiOutlineSparkles className="text-yellow-500" />
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Featured</span>
                          </div>
                          <h3 className="font-bold text-slate-800 mb-2">Job Guarantee Program</h3>
                          <p className="text-xs text-slate-500 leading-relaxed mb-4">
                            Get placed in top companies with our specialized training and placement support.
                          </p>
                        </div>
                        <Link href="/contact" className="text-sm font-semibold text-blue-600 flex items-center gap-1 group">
                          Enquire Now <FaArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </li>
        );
      })}
    </ul>
  );
};

// --- Mobile Menu Component (Redesigned) ---
const MobileMenu = ({ isOpen, setIsOpen, currentPath }) => {
  const [expanded, setExpanded] = useState(false);

  // Lock Body Scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen]);

  const sidebarVariants = {
    closed: { x: "100%", opacity: 0 },
    open: { x: 0, opacity: 1, transition: { type: "spring", stiffness: 300, damping: 30 } }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial="closed"
          animate="open"
          exit="closed"
          variants={sidebarVariants}
          className="fixed inset-0 z-[5000] bg-white flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-slate-100">
            <Link href="/" onClick={() => setIsOpen(false)}>
              {/* Ensure this logo path is correct */}
              <Image src={'/images/vidyastu-logo.png'} width={130} height={40} alt="Vidyastu" className="w-32" />
            </Link>
            <button 
              onClick={() => setIsOpen(false)}
              className="p-2 bg-slate-50 text-slate-600 rounded-full hover:bg-slate-100 transition-colors"
            >
              <AiOutlineClose size={24} />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-5 pb-32">
            <nav className="flex flex-col gap-2">
              {navigation.map((item) => (
                <div key={item.name}>
                  {item.type === "dropdown" ? (
                    <div className="rounded-xl overflow-hidden bg-slate-50/50 border border-slate-100">
                      <button
                        onClick={() => setExpanded(!expanded)}
                        className={`w-full flex items-center justify-between p-4 text-left font-semibold transition-colors ${expanded ? "text-blue-600" : "text-slate-800"}`}
                      >
                        <span className="flex items-center gap-2">
                          <FaArrowRight className={`text-xs opacity-50 ${expanded ? "rotate-90" : "rotate-0"} transition-transform`} /> 
                          {item.name}
                        </span>
                      </button>
                      
                      <AnimatePresence>
                        {expanded && (
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: "auto" }}
                            exit={{ height: 0 }}
                            className="overflow-hidden bg-white"
                          >
                            <div className="p-2 grid gap-2">
                              {premiumCourses.map((course) => (
                                <Link
                                  key={course.name}
                                  href={course.href}
                                  onClick={() => setIsOpen(false)}
                                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-blue-50 active:bg-blue-100 transition-colors"
                                >
                                  <span className={`p-2 rounded-md ${course.bg} ${course.color} text-sm`}>
                                    {course.icon}
                                  </span>
                                  <span className="text-sm font-medium text-slate-700">
                                    {course.name}
                                  </span>
                                </Link>
                              ))}
                              <Link
                                href="/premium-courses/"
                                onClick={() => setIsOpen(false)}
                                className="flex items-center justify-center gap-2 p-3 mt-1 rounded-lg bg-blue-600 text-white text-sm font-semibold active:bg-blue-700 transition-colors group"
                              >
                                View All Courses <FaArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                              </Link>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      href={item.href}
                      onClick={() => setIsOpen(false)}
                      className={`block p-4 rounded-xl font-semibold transition-all active:scale-[0.98] 
                        ${currentPath === item.href 
                          ? "bg-blue-600 text-white shadow-lg shadow-blue-200" 
                          : "text-slate-600 hover:bg-slate-50"
                        }`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </nav>
          </div>

          {/* Sticky Bottom Actions */}
          <div className="absolute bottom-0 left-0 right-0 p-5 bg-white border-t border-slate-100 shadow-[0_-10px_40px_rgba(0,0,0,0.05)]">
            <div className="flex gap-3 mb-4">
              <Link 
                href="https://wa.me/917450010203" 
                target="_blank"
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-50 text-green-700 font-bold rounded-xl border border-green-200"
              >
                <FaWhatsapp size={18} /> Chat
              </Link>
              <a 
                href="tel:+917450010203" 
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-50 text-blue-700 font-bold rounded-xl border border-blue-200"
              >
                <AiOutlinePhone size={18} /> Call
              </a>
            </div>
            <Link
              href="https://admin-panel.vidyastu.in/"
              target="_blank"
              className="flex items-center justify-center w-full py-4 bg-slate-900 text-white font-bold rounded-xl shadow-lg active:scale-[0.98] transition-transform"
            >
              Student Portal Login <HiOutlineExternalLink className="ml-2" />
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// --- Main Navbar Layout ---
const Navbar = () => {
  const path = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest > 20) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  });

  return (
    <>
      <motion.header
        className="fixed top-0 left-0 right-0 z-[1000] flex justify-center items-start pt-4 px-4 pointer-events-none"
      >
        <motion.div 
          initial={{ y: 0, width: "100%" }}
          animate={{ 
            y: 0,
            width: isScrolled ? "min(1280px, 95%)" : "100%", 
            marginTop: isScrolled ? "12px" : "0px", 
            borderRadius: isScrolled ? "99px" : "0px", 
          }}
          transition={{ duration: 0.4, type: "spring", stiffness: 100, damping: 20 }}
          className={`
            pointer-events-auto w-full transition-colors duration-500
            ${isScrolled 
              ? "bg-white/90 backdrop-blur-xl shadow-lg shadow-black/5 border border-white/40" 
              : "bg-transparent border-b border-white/5" 
            }
          `}
          style={{ maxWidth: isScrolled ? "1280px" : "100%" }}
        >
          <div className="px-6 lg:px-8 py-3 flex items-center justify-between">
            
            {/* Logo Section */}
            <div className="flex-shrink-0 flex items-center">
              <Link href="/" className="flex flex-col leading-none group">
                <Image
                  src={'/images/vidyastu-logo.png'}
                  width={150}
                  height={40}
                  alt="Vidyastu"
                  priority
                  className={`w-32 lg:w-36 h-auto object-contain transition-all duration-300 ${isScrolled ? '' : 'brightness-0 invert'}`}
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <DesktopMenu currentPath={path} scrolled={isScrolled} />

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              <Link
                href="https://admin-panel.vidyastu.in/"
                target="_blank"
                className={`hidden lg:flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 shadow-sm
                  ${isScrolled 
                    ? "bg-slate-900 text-white hover:bg-slate-800 hover:shadow-md" 
                    : "bg-white text-slate-900 hover:bg-blue-50"
                  }
                `}
              >
                <span>Student Login</span>
                <HiOutlineExternalLink className="text-lg" />
              </Link>

              {/* Mobile Menu Toggle */}
              <button 
                onClick={() => setIsMobileMenuOpen(true)}
                className={`lg:hidden p-2 rounded-full transition-colors ${isScrolled ? 'text-slate-800 hover:bg-slate-100' : 'text-white hover:bg-white/10'}`}
                aria-label="Open Menu"
              >
                <AiOutlineMenu size={24} />
              </button>
            </div>

          </div>
        </motion.div>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <MobileMenu 
        isOpen={isMobileMenuOpen} 
        setIsOpen={setIsMobileMenuOpen} 
        currentPath={path} 
      />
    </>
  );
};

export default Navbar;